import { useEffect, useState } from "react"
import { v4 as uuidv4 } from "uuid"
import { TextArea } from "components/ui/inputs/TextArea"
import { Modal } from "components/ui/modals/Modal/Modal"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { useGeneralFeedbackStore, useThankYouMessageStore } from "store/drawer.store"
import { Screenshots } from "./layout/Screenshots/Screenshots"
import { Form } from "antd"
import { FeedbackThankYou } from "components/ui/FeedbackThankYou/FeedbackThankYou"
import { Spin } from "components/ui/spin/Spin"
import { useFeedbackMutation } from "hooks/api/feedback/feedback.hook"
import { Input } from "../inputs/Input"
import type { TDrawingStatus } from "types/drawer.type"
import styles from "./NegativeFeedbackModal.module.scss"

const {
	negativeFeedbackModal,
	textGray,
	textAreaWrapper,
	screenshotWrapper,
	submitButton,
	textSmall,
	title,
	pagesInput,
	textBlack,
} = styles

type Props = {
	isModalOpen: boolean
	setModalClose: () => void
	drawingId: number
	processingId: number | string | null
	processingUrl: string
	drawingStatus: TDrawingStatus
	titleText: string
}

export interface IExtendedFeedbackFile extends File {
	preview: string
	id: string
}

// Max size for image
const imagesMaxSize = import.meta.env.VITE_UPLOADING_FEEDBACK_TOTAL_SIZE
const imagesMaxSizeInMB = imagesMaxSize / (1024 * 1024)

export const NegativeFeedbackModal = ({
	isModalOpen,
	setModalClose,
	drawingId,
	processingId,
	processingUrl,
	drawingStatus,
	titleText,
}: Props) => {
	const [submittable, setSubmittable] = useState(false)
	const [files, setFiles] = useState<IExtendedFeedbackFile[]>([])
	const { setGeneralFeedback } = useGeneralFeedbackStore()
	const { setThankYouMessage, thankYouMessage } = useThankYouMessageStore()

	const [form] = Form.useForm()
	const values = Form.useWatch([], form)

	// Custom hook to submit feedback
	const { submitFeedback, isFeedbackPending } = useFeedbackMutation({
		onSuccess: () => {
			form.resetFields() // Clear inputs
			setFiles([])
			setGeneralFeedback("thumbsDown") // Set feedback type
			setThankYouMessage(true) // Show thank you message

			// Clear feedback type after 1 second
			setTimeout(() => {
				setGeneralFeedback("")
			}, 1000)
		},
		onError: () => {
			setSubmittable(true)
		},
	})

	const onFilesAdded = (newFiles: IExtendedFeedbackFile[]) => {
		setFiles((prevFiles) =>
			prevFiles.concat(
				newFiles.map((file) =>
					Object.assign(file, {
						preview: URL.createObjectURL(file),
						id: uuidv4(),
					}),
				),
			),
		)
	}

	// Submit feedback to server
	const onSubmitHandle = () => {
		const formData = new FormData()
		formData.append("drawingId", String(drawingId))
		formData.append("processingId", String(processingId))

		// Append pages to feedback text if provided
		let feedbackText = values.feedback
		if (values.pages && values.pages.trim()) {
			feedbackText += `\n[PAGES]\n${values.pages.trim()}`
		}

		formData.append("feedback_text", feedbackText)
		formData.append("type", "NEGATIVE")
		formData.append("processing_url", processingUrl)

		files.forEach((file: IExtendedFeedbackFile) => {
			formData.append("screenshots", file)
		})

		submitFeedback({ formData, processingId, drawingId }, drawingStatus)
	}

	// Removing images
	const removeImageHandle = (id: string) => {
		setFiles((prevFiles) => prevFiles.filter((file) => file.id !== id))
	}

	// Validate screenshots
	const validateScreenshots = () => {
		const maxCount = 10
		const totalImagesSize = files.reduce((acc, file) => acc + file.size, 0)
		const exceedsMaxCount = files.length > maxCount
		const oversizedFiles = totalImagesSize > imagesMaxSize

		if (oversizedFiles) {
			return Promise.reject(new Error(`Total images size must not exceed ${imagesMaxSizeInMB}MB`))
		}
		if (exceedsMaxCount) {
			return Promise.reject(new Error(`You can only add up to ${maxCount} images`))
		}
		return Promise.resolve()
	}

	useEffect(() => {
		form
			.validateFields({ validateOnly: true })
			.then(() => {
				setSubmittable(true)
			})
			.catch(() => {
				setSubmittable(false)
			})
	}, [values, files, form])

	// Disable submit button if request is pending
	useEffect(() => {
		if (isFeedbackPending) {
			setSubmittable(false)
		}
	}, [isFeedbackPending])

	useEffect(() => {
		return () =>
			files.forEach((file) => {
				if (file.preview) {
					URL.revokeObjectURL(file.preview)
				}
			})
	}, [files])

	return (
		<Modal isModalOpen={isModalOpen} setModalClose={setModalClose} width={533}>
			{thankYouMessage ? (
				<FeedbackThankYou
					title="Thank you for your feedback!"
					text="Our team will keep you updated via email with the outcome. Feel free to give feedback as many times as
				necessary."
				/>
			) : (
				<Form form={form} onFinish={onSubmitHandle}>
					<div className={negativeFeedbackModal}>
						<span className={title}>{titleText}</span>
						<span className={textGray}>
							Describe the problem, and our team will work on resolving it as quickly as possible.
						</span>
						<Form.Item className={textAreaWrapper} name="feedback" rules={[{ required: true }]}>
							<TextArea placeholder="Share your feedback" minRows={5} />
						</Form.Item>
						<span className={textBlack}>Pages with the issue</span>
						<Form.Item name="pages">
							<Input className={pagesInput} placeholder="Specify pages with the issue" />
						</Form.Item>
						<Form.Item className={screenshotWrapper} name="screenshots" rules={[{ validator: validateScreenshots }]}>
							<Screenshots files={files} onFilesAdded={onFilesAdded} onRemoveFile={removeImageHandle} />
						</Form.Item>
						<span className={`${textGray} ${textSmall}`}>
							{`Upload up to 10 files, total files size must not exceed ${imagesMaxSizeInMB}MB, acceptable file types .png, .jpeg, .jpg`}
						</span>
						<ButtonPrimary submittable={submittable} className={submitButton}>
							{isFeedbackPending ? <Spin size="default" /> : "Send"}
						</ButtonPrimary>
					</div>
				</Form>
			)}
		</Modal>
	)
}
